import React from 'react';
import formatWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { occupancyMapping } from 'Feature/V2/HbtPortfolioManagement/utils/OccupancyMapping';
import { requestTypeMapping } from 'Feature/V2/HbtPortfolioManagement/utils/RequestTypeMapping';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { TFunction } from 'i18next';
import { InsuredApprovedLenderName } from '../../HbtPortfolioManagementPage/types';
import HBTTableCell from '../components/HBTTableCell';
import { TranslationType } from '../types';
import i18n from 'i18next';
import formatDigits from '../../HbtPortfolioManagementRequestOverview/components/PortfolioCard/utils/FormatDigits';
import { getStatusLabel } from '../utils/GetStatusLabel';
import { statusMapping } from 'Feature/V2/HbtPortfolioManagement/utils/StatusMapping';
import StatusPill from '../../StatusPill';
import styles from '../styles.module.scss';

const getOccupancyTypeLabel = (
  occupancyType: keyof typeof occupancyMapping,
  t: TranslationType
) => {
  return occupancyMapping(t)[occupancyType];
};

const getRequestTypeLabel = (requestType: keyof typeof requestTypeMapping, t: TranslationType) => {
  return requestTypeMapping(t)[requestType];
};

const getInsuredApprovedlenderName = (data: InsuredApprovedLenderName, language: string) => {
  return language === 'en' ? data.englishName : data.frenchName;
};

const getTermInsuranceLabel = (
  data: number,
  t: TFunction<'HbtPortfolioManagementPage', undefined>
) => {
  const termInsurance = data ? data.toString() : '';
  const termValue = termInsurance.trim() === '' ? 0 : Number(termInsurance);
  let suffix = '';
  if (termInsurance === '' || termValue === 0) {
    suffix = '';
  } else if (termValue === 1) {
    suffix = t('year');
  } else {
    suffix = t('years');
  }
  return termInsurance + (suffix ? ` ${suffix}` : '');
};

export const createInsuredLenderNameRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const data = cell.getValue();
    const displayName = getInsuredApprovedlenderName(data, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={data?.englishName || ''}
        cellContent={<HBTTableCell title={displayName}>{displayName}</HBTTableCell>}
      />
    );
  };

export const createOccupancyTypeRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    const label = getOccupancyTypeLabel(value as keyof typeof occupancyMapping, t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={value as string}
        cellContent={<HBTTableCell title={label}>{label}</HBTTableCell>}
      />
    );
  };

export const createTermInsuranceRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    const label = getTermInsuranceLabel(value, t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={String(value)}
        cellContent={<HBTTableCell title={label}>{label}</HBTTableCell>}
      />
    );
  };

export const createRequestTypeRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    const label = getRequestTypeLabel(value as keyof typeof requestTypeMapping, t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={value as string}
        cellContent={<HBTTableCell title={label}>{label}</HBTTableCell>}
      />
    );
  };

export const createAmountSubmittedRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const amount = cell.getValue();
    const formattedAmount = formatWithDollar(amount, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={formattedAmount}
        cellContent={<HBTTableCell title={formattedAmount}>{formattedAmount}</HBTTableCell>}
      />
    );
  };

export const renderApplicationIdCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return <HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>;
};

export const renderPortfolioIdsCell = ({ cell }: { cell: any }, t: TFunction) => {
  const value = cell.getValue();
  const portfolioIds = String(value)
    .split(',')
    .map((id: string) => id.trim());
  const translationFn = t as TranslationType;
  const currentStatusMapping = statusMapping(translationFn);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '32px',
        flexWrap: 'wrap',
        overflow: 'hidden',
        maxWidth: '100%'
      }}
    >
      {portfolioIds.map((item: string, index: number) => {
        const [rawId, rawStatus] = item.split(':');
        const id = rawId.trim();
        const status = rawStatus?.trim() || '';
        const statusKey = status as keyof ReturnType<typeof statusMapping>;
        const statusLabel =
          status && currentStatusMapping[statusKey] ? getStatusLabel(statusKey, translationFn) : '';

        return (
          <div
            key={`${id}-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100px',
                display: 'inline-block'
              }}
            >
              {id}
            </span>
            <div className={styles.statusPillWrapper}>
              <StatusPill status={status} label={statusLabel} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const renderReceiptDateCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return (
    <MemoizedHbtDataTableCell
      dataValue={value as string}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

export const renderInsuredLenderCodeCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return (
    <MemoizedHbtDataTableCell
      dataValue={value as string}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

export const renderNumberOfLoansCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  const displayValue = formatDigits(value, i18n.language);
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={displayValue}>{displayValue}</HBTTableCell>}
    />
  );
};
